import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { CommonModule } from '@angular/common';
import { MatIconModule } from '@angular/material/icon';
import { RouterLink } from '@angular/router';

import { EntityMatchBlock } from '@core/services/ai/ai-assistant.service';
import { buildEntityDeeplink, defaultIconFor } from '../entity-deeplink.helper';

@Component({
  selector: 'app-ai-entity-match-block',
  standalone: true,
  changeDetection: ChangeDetectionStrategy.OnPush,
  imports: [CommonModule, MatIconModule, RouterLink],
  template: `
    <ng-container *ngTemplateOutlet="body"></ng-container>
    <ng-template #body>
      @if (deeplink()) {
        <a [routerLink]="deeplink()" class="card card--clickable">
          <ng-container *ngTemplateOutlet="content"></ng-container>
        </a>
      } @else {
        <div class="card">
          <ng-container *ngTemplateOutlet="content"></ng-container>
        </div>
      }
    </ng-template>

    <ng-template #content>
      <mat-icon class="card__icon">{{ icon() }}</mat-icon>
      <div class="card__body">
        <div class="card__title">{{ block.title }}</div>
        @if (block.subtitle) {
          <div class="card__subtitle">{{ block.subtitle }}</div>
        }
        @if (block.meta?.length) {
          <div class="card__meta">
            @for (m of block.meta; track m.label) {
              <span class="card__meta-item">
                <span class="card__meta-label">{{ m.label }}:</span>
                <span class="card__meta-value">{{ m.value }}</span>
              </span>
            }
          </div>
        }
      </div>
    </ng-template>
  `,
  styles: [`
    :host { display: block; }
    .card {
      display: flex;
      align-items: flex-start;
      gap: 12px;
      padding: 12px 14px;
      background: #fff;
      border: 1px solid rgba(15, 23, 42, 0.08);
      border-left: 3px solid #6366f1;
      border-radius: 12px;
      text-decoration: none;
      color: inherit;
    }
    .card--clickable:hover {
      background: rgba(99, 102, 241, 0.04);
    }
    .card__icon {
      font-size: 22px;
      width: 22px;
      height: 22px;
      color: #6366f1;
      flex-shrink: 0;
    }
    .card__body { flex: 1; min-width: 0; }
    .card__title {
      font-weight: 600;
      font-size: 14px;
      color: rgba(15, 23, 42, 0.92);
      overflow-wrap: anywhere;
      word-break: break-word;
    }
    .card__subtitle {
      font-size: 13px;
      color: rgba(15, 23, 42, 0.6);
      margin-top: 2px;
      overflow-wrap: anywhere;
    }
    .card__meta {
      margin-top: 6px;
      display: flex;
      flex-wrap: wrap;
      gap: 12px;
      font-size: 12px;
    }
    .card__meta-label {
      color: rgba(15, 23, 42, 0.5);
      text-transform: uppercase;
      letter-spacing: 0.04em;
      margin-right: 4px;
    }
    .card__meta-value {
      color: rgba(15, 23, 42, 0.8);
      font-weight: 600;
    }
  `]
})
export class AiEntityMatchBlockComponent {
  @Input({ required: true }) block!: EntityMatchBlock;

  icon(): string {
    return this.block.icon || defaultIconFor(this.block.entityType);
  }

  deeplink(): string | null {
    return buildEntityDeeplink(this.block.entityType, this.block.entityId);
  }
}
